import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MessageCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { Event } from '@/types';
import { eventService } from '@/services/eventService';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

interface Guest {
  id: string;
  nombre: string;
  tel: string;
  estado: string;
}

// Extend the WhatsAppTemplate type to include the template_id field
type WhatsAppTemplateBase = Database['public']['Tables']['whatsapp_templates']['Row'];
type WhatsAppTemplate = WhatsAppTemplateBase & { template_id?: string };

interface WhatsAppInviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guests: Guest[];
  event: Event;
}

export const WhatsAppInviteModal: React.FC<WhatsAppInviteModalProps> = ({ 
  open, 
  onOpenChange, 
  guests,
  event 
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Step 1: Guest selection
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  
  // Step 2: Template and variables
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [variableValues, setVariableValues] = useState<string[]>([]);
  const [previewMessage, setPreviewMessage] = useState<string>('');
  
  // Other state
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setSelectedGuests([]);
      setSelectedTemplate(null);
      setVariableValues([]);
      fetchTemplates();
    }
  }, [open]);

  // Fetch available templates
  const fetchTemplates = async () => {
    try {
      setTemplateLoading(true);
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .eq('estado', 'ACTIVO')
        .order('nombre');

      if (error) {
        throw error;
      }

      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las plantillas de WhatsApp',
        variant: 'destructive'
      });
    } finally {
      setTemplateLoading(false);
    }
  };

  // Update preview message when template or variables change
  useEffect(() => {
    if (selectedTemplate) {
      let message = selectedTemplate.texto;
      
      // Replace variables in the message
      variableValues.forEach((value, index) => {
        const placeholder = `{{${index + 1}}}`;
        // Ensure proper escaping of regex special characters
        const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedPlaceholder, 'g');
        
        // If value is empty, keep the placeholder but style it differently in the UI
        message = message.replace(regex, value ? `<span class="placeholder">${value}</span>` : `<span class="placeholder">${placeholder}</span>`);
      });
      
      setPreviewMessage(message);
    }
  }, [selectedTemplate, variableValues]);

  const toggleGuest = (guestId: string) => {
    setSelectedGuests(prev => 
      prev.includes(guestId) 
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const selectAll = () => {
    const pendingGuests = guests
      .filter(g => g.estado === 'PENDIENTE')
      .map(g => g.id);
    setSelectedGuests(pendingGuests);
  };

  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    
    // Initialize variable values array with empty strings
    const newVariableValues = Array(template.variables_count).fill('');
    setVariableValues(newVariableValues);
  };

  const handleVariableChange = (index: number, value: string) => {
    setVariableValues(prev => {
      const newValues = [...prev];
      newValues[index] = value;
      return newValues;
    });
  };

  const handleNextStep = () => {
    setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSendMessages = async () => {
    if (!event?.id || selectedGuests.length === 0 || !selectedTemplate) return;
    
    try {
      setIsSending(true);
      
      // Call the Supabase Edge Function to send WhatsApp invites
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sendInvites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          guestIds: selectedGuests,
          eventId: event.id,
          templateId: selectedTemplate.template_id,
          variables: variableValues,
          useTemplate: true
        })
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      onOpenChange(false);
      toast({
        title: 'Mensajes enviados',
        description: `Se han enviado mensajes a ${selectedGuests.length} invitados`,
      });
    } catch (error) {
      console.error('Error sending WhatsApp invites:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron enviar los mensajes de WhatsApp',
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const pendingGuests = guests.filter(g => g.estado === 'PENDIENTE');
  const allVariablesFilled = variableValues.every(value => value.trim() !== '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <style>{`
          .placeholder {
            background-color: #ffecb3;
            padding: 2px 4px;
            border-radius: 4px;
            color: #664d03;
            font-weight: 500;
          }
        `}</style>
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <MessageCircle className="w-5 h-5 mr-2 text-green-600" />
            {currentStep === 1 ? 'Seleccionar Invitados' : 'Personalizar Mensaje'}
          </DialogTitle>
        </DialogHeader>
        
        {/* Step 1: Select Guests */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Selecciona los invitados que deseas contactar:
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={selectAll}
              >
                Seleccionar pendientes
              </Button>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-2">
              {guests.map((guest) => (
                <div key={guest.id} className="flex items-center space-x-3 p-2 border rounded">
                  <Checkbox
                    checked={selectedGuests.includes(guest.id)}
                    onCheckedChange={() => toggleGuest(guest.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{guest.nombre}</p>
                    <p className="text-sm text-gray-600">{guest.tel}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    guest.estado === 'PENDIENTE' ? 'bg-amber-100 text-amber-800' :
                    guest.estado === 'CONFIRMADO' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {guest.estado}
                  </span>
                </div>
              ))}
              
              {guests.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No hay invitados registrados
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleNextStep}
                disabled={selectedGuests.length === 0}
                className="bg-brand-primary hover:bg-brand-primary/90"
              >
                Siguiente paso
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 2: Select Template and Fill Variables */}
        {currentStep === 2 && (
          <div className="space-y-4">
            {/* Template selection */}
            <div>
              <Label className="block mb-2">Selecciona una plantilla:</Label>
              <div className="max-h-40 overflow-y-auto border rounded p-2">
                {templateLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary"></div>
                  </div>
                ) : templates.length > 0 ? (
                  templates.map(template => (
                    <div 
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`p-2 mb-1 rounded cursor-pointer ${
                        selectedTemplate?.id === template.id 
                          ? 'bg-brand-primary/10 border border-brand-primary' 
                          : 'hover:bg-gray-100 border'
                      }`}
                    >
                      <div className="font-medium">{template.nombre}</div>
                      <div className="text-xs text-gray-600">{template.description}</div>
                      {template.template_id && (
                        <div className="text-xs text-blue-600">ID: {template.template_id}</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    No hay plantillas disponibles
                  </div>
                )}
              </div>
            </div>
            
            {/* Template variables and preview */}
            {selectedTemplate && (
              <div className="grid grid-cols-2 gap-4">
                {/* Variables input */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Variables:</h3>
                  {Array.from({ length: selectedTemplate.variables_count }).map((_, index) => (
                    <div key={index} className="space-y-1">
                      <Label htmlFor={`var-${index}`} className="text-xs">
                        Variable {index + 1}
                      </Label>
                      <Input
                        id={`var-${index}`}
                        value={variableValues[index] || ''}
                        onChange={(e) => handleVariableChange(index, e.target.value)}
                        placeholder={`Valor para {{${index + 1}}}`}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Message preview */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Vista previa:</h3>
                  <div 
                    className="bg-gray-50 p-3 rounded border min-h-[150px] whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: previewMessage }}
                  />
                </div>
              </div>
            )}
            
            {/* Buttons */}
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handlePreviousStep}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={!selectedTemplate || !allVariablesFilled || isSending || !selectedTemplate.template_id}
                onClick={handleSendMessages}
              >
                {isSending ? 'Enviando...' : `Enviar a ${selectedGuests.length} invitado(s)`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
